import { Pagination, PaginationResponse, User, UserCreatePayload, UserUpdatePayload } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, SmartToyOutlined, SupervisedUserCircle } from '@mui/icons-material';
import { Button, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminTable, AdminTableColumn, Highlight } from 'src/Common/Components/AdminTable';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import { AuthMechanisms } from 'src/Common/Helper/AuthMechanisms';
import useRequest from 'src/Common/Hooks/useRequest';
import Permissions from 'src/Common/Permissions';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { orange, red } from '@mui/material/colors';
import { SettingsSection } from '../Components/SettingsSection';
import { UsersAdminDrawer } from '../../UserManagement/Components/UsersAdminDrawer';
import { useSettingsSection } from '../Providers/SettingsSectionProvider';

type DialogKind = 'delete' | 'valid' | undefined;

export const UsersAdminSection = (): React.JSX.Element => {
  const { setDrawerOpen } = useSettingsSection();
  const { api, hasPermission, config } = useEkko();

  const navigate = useNavigate();
  const { refresh: triggerRefresh } = usePagination();
  const [, startSaving] = useRequest<User>();

  const [dialog, setDialog] = useState<DialogKind>();
  const [selectedUser, setSelectedUser] = useState<User | undefined>();

  const [hasUsersWrite, hasMappingsRead] = useMemo(
    () => [hasPermission(Permissions.USERS_WRITE), hasPermission(Permissions.MAPPINGS_READ)],
    [hasPermission],
  );

  const fetchItems = useCallback(
    (pagination: Pagination): Promise<PaginationResponse<User>> => api.getUsers(pagination, true, true),
    [api],
  );

  const dismiss = useCallback(() => {
    setDrawerOpen(false);
    setDialog(undefined);
    setSelectedUser(undefined);
  }, []);

  const saveHandler = useCallback(
    async (user: UserUpdatePayload | UserCreatePayload, password?: string) => {
      const isUpdate = (user as UserUpdatePayload).id !== undefined;
      startSaving(
        () =>
          isUpdate
            ? api.updateUser(user as UserUpdatePayload, password)
            : api.addUser(user as UserCreatePayload, password),
        () => {
          enqueueSnackbar(isUpdate ? 'User updated.' : 'User created.');
          dismiss();
          triggerRefresh();
        },
      );
    },
    [api, startSaving, triggerRefresh],
  );

  const onDeleteConfirm = useCallback(async () => {
    if (!selectedUser?.id) {
      return;
    }
    await api.deleteUser(selectedUser);
    dismiss();
    triggerRefresh();
  }, [api, selectedUser, triggerRefresh]);

  const onValidUntilConfirm = useCallback(async () => {
    if (selectedUser === undefined) {
      return;
    }

    await saveHandler({
      id: selectedUser.id,
      username: selectedUser.username,
      email: selectedUser.email,
      coordinate: selectedUser.coordinate,
      validUntil: selectedUser.validUntil,
    });
    dismiss();
  }, [selectedUser, saveHandler]);

  const onChangeValidUntil = useCallback(
    (date: Moment | null) => {
      if (!selectedUser || date === null) {
        return;
      }
      setSelectedUser({ ...selectedUser, validUntil: date.format('YYYY-MM-DD') });
    },
    [selectedUser],
  );

  const columns = useMemo<AdminTableColumn<User>[]>(
    () => [
      {
        field: 'username',
        headerName: 'Username',
        size: 220,
        renderCell: (row) => (
          <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ width: '100%' }}>
            <UserAvatar username={row.username} size={'small'} />
            <Typography fontWeight={600} noWrap>
              <Highlight text={row.username} field={'username'} />
            </Typography>
            {!!row.automation && (
              <Tooltip title={'automation user'}>
                <SmartToyOutlined fontSize={'small'} color={'action'} />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'email',
        headerName: 'E-Mail',
        size: 260,
        renderCell: (row) => (
          <Typography
            component={'a'}
            href={`mailto:${row.email}`}
            noWrap
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            <Highlight text={row.email} field={'email'} />
          </Typography>
        ),
      },
      {
        field: 'coordinate',
        headerName: 'Coordinate',
        size: 300,
        renderCell: (row) => (
          <Typography variant={'body2'} noWrap color={'text.secondary'}>
            <Highlight text={row.coordinate} field={'coordinate'} />
          </Typography>
        ),
      },
      {
        field: 'validUntil',
        headerName: 'Valid-Until',
        size: 240,
        sortable: false,
        filterable: false,
        renderCell: (row) => {
          if (row.automation) {
            return (
              <Chip
                label={'No expiry'}
                size={'small'}
                sx={{
                  backgroundColor: 'action.selected',
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            );
          }
          const m = moment(row.validUntil);
          const hours = m.diff(moment(), 'hours');
          const isExpired = hours <= 0;
          const isSoon = hours > 0 && hours < 48;
          return (
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Tooltip title={'Edit valid-until'}>
                <Typography
                  onClick={() => {
                    if (!hasUsersWrite || row.automation) {
                      return;
                    }
                    setSelectedUser(row);
                    setDialog('valid');
                  }}
                  sx={{
                    cursor: hasUsersWrite ? 'pointer' : 'default',
                    borderBottom: '1px dashed',
                    borderColor: 'text.secondary',
                    '&:hover': hasUsersWrite ? { borderColor: 'primary.main', color: 'primary.main' } : undefined,
                  }}
                >
                  {m.format('DD MMM YYYY')}
                </Typography>
              </Tooltip>
              <Chip
                label={m.fromNow()}
                size={'small'}
                sx={{
                  backgroundColor: isExpired ? red[500] : isSoon ? orange[500] : 'action.selected',
                  color: isExpired || isSoon ? 'white' : 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            </Stack>
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        size: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        hideInColumnsPopover: true,
        align: 'center',
        renderCell: (row) => (
          <RowActionsMenu<User>
            row={row}
            actions={[
              {
                label: 'Roles',
                icon: <SupervisedUserCircle fontSize="small" />,
                onClick: (u) => navigate('/me/admin/users/' + u.id + '/mappings'),
                disabled: () => !hasMappingsRead,
              },
              {
                label: 'Edit',
                icon: <Edit fontSize="small" />,
                onClick: (u) => {
                  setSelectedUser(u);
                  setDrawerOpen(true);
                },
                disabled: (u) => !hasMappingsRead || !!u.automation,
              },
              {
                label: 'Delete',
                icon: <Delete fontSize="small" />,
                onClick: (u) => {
                  setSelectedUser(u);
                  setDialog('delete');
                },
                disabled: () => !hasMappingsRead,
              },
            ]}
          />
        ),
      },
    ],
    [hasMappingsRead, hasUsersWrite, navigate],
  );

  const dialogElement = useMemo(() => {
    if (!dialog || !selectedUser) {
      return undefined;
    }
    if (dialog === 'delete') {
      return (
        <GenericDialog
          open
          fullWidth
          centered
          timedOkButton
          type={'confirmation'}
          maxWidth={'sm'}
          title={'Delete User'}
          text={
            <CenteredColumn>
              <Typography>{'Delete User "' + selectedUser.username + '"?'}</Typography>
              <Typography style={{ margin: 16 }} variant={'caption'}>
                This action can not be undone.
              </Typography>
            </CenteredColumn>
          }
          onClose={dismiss}
          onConfirm={onDeleteConfirm}
        />
      );
    }
    const validUntil = moment(selectedUser.validUntil);
    return (
      <GenericDialog
        open
        type={'confirmation'}
        title={'Update Valid-Until'}
        text={
          <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <UserAvatar username={selectedUser.username} size={'large'} />
              <Stack spacing={0.5}>
                <Typography fontWeight={600}>{selectedUser.username}</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  {selectedUser.email}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <DatePicker
              label={'Valid-Until'}
              disablePast
              value={validUntil}
              onChange={onChangeValidUntil}
              minDate={moment().add(1, 'd')}
              sx={{ width: '100%' }}
            />
          </Stack>
        }
        onClose={dismiss}
        onConfirm={onValidUntilConfirm}
      />
    );
  }, [dialog, selectedUser, dismiss, onDeleteConfirm, onValidUntilConfirm, onChangeValidUntil]);

  return (
    <SettingsSection
      title={'Users'}
      description={'Manage user accounts, coordinates and validity.'}
      drawer={
        <UsersAdminDrawer
          user={selectedUser}
          closeHandler={dismiss}
          saveHandler={saveHandler}
          passwordEditable={config.api.authMechanism !== AuthMechanisms.OAUTH2}
        />
      }
    >
      <AdminTable<User>
        fetchItems={fetchItems}
        columns={columns}
        initialSort={{ field: 'username', sort: 'asc' }}
        sortFieldMap={{
          username: 'U.username',
          email: 'U.email',
          coordinate: 'Node.materializedPath',
        }}
        primaryAction={
          hasUsersWrite ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedUser(undefined);
                setDrawerOpen(true);
              }}
            >
              Add user
            </Button>
          ) : undefined
        }
      />
      {dialogElement}
    </SettingsSection>
  );
};
